<template>
  <div class="t-bg-white t-border t-border-gray-100 t-shadow-md t-py-2 t-pb-4 t-px-5 t-overflow-hidden md:t-rounded-xl">
    <!-- Card Heading -->
    <div class="t-flex t-justify-between t-items-baseline t-py-2">
      <div class="t-flex t-space-x-4 t-items-center">
        <p class="t-text-2xl t-font-bold t-py-1">
          {{ offer.id  }}
        </p>
        <div
          class="t-py-1 t-px-2 t-rounded t-bg-seagreen t-text-white t-text-lg t-font-semibold t-shadow t-uppercase"
          :class="[{ 't-bg-gray-300': isOfferExpired(offer.expiry) }]"
        >
          {{ !isOfferExpired(offer.expiry) ? 'Available':'Expired' }}
        </div>
      </div>
    </div>

    <div class="t-border-t-2 t-border-b-2 t-border-gray-300 t-flex t-flex-col t-pb-2 md:t-flex-row md:t-justify-between md:t-pb-0">
      <div class="t-grid t-grid-flow-row t-auto-rows-max t-grid-cols-1 t-py-1 md:t-grid-rows-1 md:t-grid-flow-col md:t-auto-cols-fr md:t-py-3 md:t-divide-gray-300">
        <div class="md:t-border-r-2">
          <p class="t-text-gray-800 t-tracking-wide t-uppercase t-text-sm t-font-semibold">
            Pickup Requires Tail Lift ?
          </p>
          <p class="t-text-black">{{ offer.pickup_requires_taillift ? 'Yes' : 'No' }}</p>
        </div>
        <div class="md:t-px-4 md:t-border-r-2">
          <p class="t-text-gray-800 t-tracking-wide t-uppercase t-text-sm t-font-semibold">
            Delivery Requires Tail Lift ?
          </p>
          <p class="t-text-black">{{ offer.delivery_requires_taillift ? 'Yes' : 'No' }}</p>
        </div>
        <div v-if="offer.special_instructions" class="md:t-px-4 md:t-border-r-2">
          <p class="t-text-gray-700 t-tracking-wide t-uppercase t-text-sm t-font-semibold">
            Special Instructions
          </p>
          <div class="t-break-words">
            <p class="t-text-black">{{ offer.special_instructions }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Card Body -->
    <div class="t-grid t-grid-flow-row t-auto-rows-max t-grid-cols-1 t-gap-y-3 t-mt-4 md:t-grid-rows-1 md:t-grid-cols-12">
      <!-- Pickup From -->
      <div class="md:t-col-span-3">
        <p class="t-text-sm t-uppercase t-tracking-wide t-text-dodger-blue t-font-semibold t-my-1">
          From
        </p>
        <p class="t-font-semibold">{{ offer.pickup_location.name }}</p>
        <p>{{ offer.pickup_location.address.street_number }} {{ offer.pickup_location.address.street_name }}</p>
        <p>{{ offer.pickup_location.address.suburb }}</p>
        <p>{{ offer.pickup_location.address.city }}</p>

        <p class="t-mt-3">Pickup After {{ moment.utc(offer.pickup_after).local().format('hh:mm A ddd DD MMM') }}</p>
        <p v-if="offer.pickup_before">Pickup Before {{ moment.utc(offer.pickup_before).local().format('hh:mm A ddd DD MMM') }}</p>
      </div>

      <!-- Delivery To -->
      <div class="md:t-col-span-3">
        <p class="t-text-sm t-uppercase t-tracking-wide t-text-dodger-blue t-font-semibold t-my-1">
          To
        </p>
        <p class="t-font-semibold">{{ offer.delivery_location.name }}</p>
        <p>{{ offer.delivery_location.address.street_number }}  {{ offer.delivery_location.address.street_name }} </p>
        <p>{{ offer.delivery_location.address.suburb }}</p>
        <p>{{ offer.delivery_location.address.city }}</p>

        <p class="t-mt-3">Deliver By {{ moment.utc(offer.deliver_by).local().format('hh:mm A ddd DD MMM') }}</p>
      </div>

      <!-- Freight Information -->
      <div class="md:t-col-span-6">
        <p class="t-text-sm t-uppercase t-tracking-wide t-text-dodger-blue t-font-semibold t-border-b-2 t-border-gray-300 t-pb-1 t-mb-2">
          Freight
        </p>

        <div
          class="t-flex t-space-x-2"
          v-for="(freight, index) in offer.freight"
          :key="index"
        >
          <p class="">
            {{ freight.quantity }} x {{ freight.package_type }} {{ freight.goods_type }}
          </p>
          <p class="t-w-20 t-text-right">{{ Math.trunc(parseFloat(freight.weight)) }} kg</p>
          <p class="t-w-20 t-text-right">{{ parseFloat(freight.volume) }} m <span><sup>3</sup></span> </p>
        </div>

        <div class="t-flex t-font-medium t-mt-1 t-py-1 t-border-t-2 t-border-b-2 t-border-gray-300 t-space-x-2">
          <p class="t-uppercase">Total</p>
          <p class="t-w-20 t-text-right">{{ parseFloat(offer.weight) }} kg</p>
          <p class="t-w-20 t-text-right">{{ parseFloat(offer.volume) }} m<sup>3</sup> </p>
        </div>

        <div v-if="offer.dangerous_goods && offer.dangerous_goods.length > 0" class="t-mt-1">
          <p class="t-font-medium">Contains Dangerous Goods</p>
          <p v-for="(dangerousGood, index) in offer.dangerous_goods" :key="index">
            {{ dangerousGood.quantity }} x {{ dangerousGood.name }} ({{ dangerousGood.type }})
            <span>
              <span class="t-ml-2 t-font-semibold">UN:</span> {{ dangerousGood.un_id }}
              <span class="t-ml-2 t-font-semibold">Class:</span> {{ dangerousGood.un_class }}
            </span>
          </p>
        </div>
      </div>
    </div>

    <div class="t-mt-3 t--mx-5 t--mb-4 t-px-5 t-pb-4 t-pt-3 t-border-t t-flex t-justify-end">
      <div>
        <div class="t-flex t-justify-end">
          <div class="t-space-x-4 t-mt-2">
            <p class="t-inline-block">
              <span class="t-font-medium">
                Offer Expiry:
              </span>
              {{ moment.utc(offer.expiry).local().fromNow() }} ({{ moment.utc(offer.expiry).local().format('hh:mm A ddd DD MMM') }})
            </p>
            <p class="t-inline-block">
              <span class="t-font-medium">
                Price:
              </span>
              ${{ offer.cost }}
            </p>
            <button
              @click="onDeclineClick"
              class="bg-danger t-rounded-md t-text-white t-font-medium t-py-2 t-px-3"
            >
              Decline
            </button>
            <button
              @click="onAcceptClick"
              class="bg-success t-rounded-md t-text-white t-font-medium t-py-2 t-px-3"
            >
              Accept
            </button>
          </div>
        </div>
      </div>
    </div>


  </div>
</template>

<script>
import moment from "moment";

export default {
  name: 'FreightOfferCard',
  emits: ['acceptOfferClicked', 'declineOfferClicked'],
  props: {
    offer: {
      type: Object
    }
  },
  data() {
    return {
      moment
    }
  },
  methods: {
    onAcceptClick() {
      this.$emit('acceptOfferClicked', this.offer);
    },
    onDeclineClick() {
      this.$emit('declineOfferClicked', this.offer);
    },
    isOfferExpired(expiry) {
      return moment.utc(expiry).local().isBefore(moment.now());
    }
  }
}
</script>

<style scoped>

</style>
